import { mapPlayerLook } from '@/lib/api/rewards-hub/mappers';
import type {
  ActiveOrder,
  ActiveOrderPageResponse,
  ActivityItem,
  ActivityPageResponse,
  FullActivityItem,
  FullActivityListResponse,
  FullOrderHistory,
  FullOrderHistoryListResponse,
  LastTradeStatsItem,
  LastTradeStatsResponse,
  OrderHistory,
  PageResponse,
  TradeHistory,
  TradeHistoryFull,
  TradeHistoryFullListResponse,
} from './models';
import type {
  ActiveOrderDto,
  ActiveOrderPageResponseDto,
  ActivityItemDto,
  ActivityPageResponseDto,
  FullActivityItemDto,
  FullActivityListResponseDto,
  FullOrderHistoryDto,
  FullOrderHistoryListResponseDto,
  LastTradeStatsItemDto,
  LastTradeStatsResponseDto,
  OrderHistoryDto,
  PageResponseDto,
  TradeHistoryDto,
  TradeHistoryFullDto,
  TradeHistoryFullListResponseDto,
  TradeHistoryPageResponseDto,
} from './types';

/**
 * Maps OrderHistoryDto (short field names) to OrderHistory (readable field names)
 */
export function mapOrderHistory(dto: OrderHistoryDto): OrderHistory {
  return {
    id: dto.i,
    userId: dto.u,
    eventId: dto.e,
    eventOutcomeId: dto.o,
    side: dto.si,
    type: dto.t,
    price: dto.p,
    placementTime: dto.m,
    status: dto.st,
    quantity: dto.q,
    ...(dto.oq !== undefined && { originalQuantity: dto.oq }),
    ...(dto.mq !== undefined && { matchedQuantity: dto.mq }),
    expireAt: dto.ex,
  };
}

/**
 * Maps TradeHistoryDto (short field names) to TradeHistory (readable field names)
 */
export function mapTradeHistory(dto: TradeHistoryDto): TradeHistory {
  return {
    id: dto.i,
    buyerId: dto.b,
    sellerId: dto.s,
    contractId: dto.c,
    dealId: dto.d,
    eventId: dto.e,
    eventOutcomeId: dto.o,
    assetId: dto.a,
    quantity: dto.q,
    price: dto.p,
    fee: dto.f,
    revenue: dto.r,
    matchTime: dto.m,
  };
}

/**
 * Maps PageResponseDto to PageResponse, applying item mapper to each content item
 */
export function mapPageResponse<TDto, TModel>(
  dto: PageResponseDto<TDto>,
  itemMapper: (dto: TDto) => TModel,
): PageResponse<TModel> {
  return {
    content: dto.content.map(itemMapper),
    totalElements: dto.totalElements,
    totalPages: dto.totalPages,
    size: dto.size,
    number: dto.number,
    numberOfElements: dto.numberOfElements,
    first: dto.first,
    last: dto.last,
    empty: dto.empty,
  };
}

// Compact format type for order history (similar to ActiveOrderPageResponseDto)
interface OrderHistoryCompactPageResponseDto {
  i: OrderHistoryDto[]; // items array
  p: number; // page number
  s: number; // size
  tp: number; // totalPages
}

/**
 * Maps OrderHistoryPageResponseDto to OrderHistoryPageResponse
 * Note: API returns compact format {i: items[], p: page, s: size, tp: totalPages}
 */
export function mapOrderHistoryPageResponse(dto: unknown): PageResponse<OrderHistory> {
  if (!dto) {
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      size: 0,
      number: 0,
      numberOfElements: 0,
      first: true,
      last: true,
      empty: true,
    };
  }

  // Type guard for compact format
  const isCompactFormat = (obj: unknown): obj is OrderHistoryCompactPageResponseDto => {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'i' in obj &&
      Array.isArray((obj as OrderHistoryCompactPageResponseDto).i)
    );
  };

  // Type guard for standard format
  const isStandardFormat = (obj: unknown): obj is PageResponseDto<OrderHistoryDto> => {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'content' in obj &&
      Array.isArray((obj as PageResponseDto<OrderHistoryDto>).content)
    );
  };

  // Check if it's compact format (has 'i' field)
  if (isCompactFormat(dto)) {
    return {
      content: dto.i.map(mapOrderHistory),
      totalElements: dto.i.length,
      totalPages: dto.tp || 0,
      size: dto.s || 0,
      number: dto.p || 0,
      numberOfElements: dto.i.length,
      first: dto.p === 0,
      last: dto.p >= dto.tp - 1,
      empty: dto.i.length === 0,
    };
  }

  // Check if it's standard format (has 'content' field)
  if (isStandardFormat(dto)) {
    return mapPageResponse(dto, mapOrderHistory);
  }

  return {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 0,
    number: 0,
    numberOfElements: 0,
    first: true,
    last: true,
    empty: true,
  };
}

/**
 * Maps TradeHistoryPageResponseDto to TradeHistoryPageResponse
 * TradeHistoryPageResponseDto uses compact format: {i: TradeHistoryDto[], p: number, s: number, tp: number}
 */
export function mapTradeHistoryPageResponse(
  dto: TradeHistoryPageResponseDto,
): PageResponse<TradeHistory> {
  return {
    content: dto.i.map(mapTradeHistory),
    totalElements: dto.i.length, // Approximate, since backend doesn't provide totalElements
    totalPages: dto.tp,
    size: dto.s,
    number: dto.p,
    numberOfElements: dto.i.length,
    first: dto.p === 0,
    last: dto.p >= dto.tp - 1,
    empty: dto.i.length === 0,
  };
}

/**
 * Maps ActiveOrderDto (short field names) to ActiveOrder (readable field names)
 */
export function mapActiveOrder(dto: ActiveOrderDto): ActiveOrder {
  return {
    id: dto.i,
    userId: dto.u,
    eventId: dto.e,
    eventOutcomeId: dto.o,
    assetId: dto.a,
    originalQuantity: dto.oq,
    side: dto.si,
    type: dto.t,
    price: dto.p,
    matchedQuantity: dto.m,
    status: dto.st,
    quantity: dto.q,
    expireAt: dto.ex,
  };
}

/**
 * Maps ActiveOrderPageResponseDto to ActiveOrderPageResponse
 */
export function mapActiveOrderPageResponse(
  dto: ActiveOrderPageResponseDto,
): ActiveOrderPageResponse {
  return {
    items: dto.i.map(mapActiveOrder),
    page: dto.p,
    size: dto.s,
    totalPages: dto.tp,
  };
}

/**
 * Maps ActivityItemDto (short field names) to ActivityItem (readable field names)
 */
export function mapActivityItem(dto: ActivityItemDto): ActivityItem {
  return {
    transactionId: dto.tid,
    userId: dto.u,
    username: dto.n,
    outcomeId: dto.oi,
    outcomeName: dto.on,
    position: dto.p,
    side: dto.si,
    shares: dto.q,
    executedAt: dto.x,
    look: mapPlayerLook(dto.lk),
  };
}

/**
 * Maps ActivityPageResponseDto to ActivityPageResponse
 */
export function mapActivityPageResponse(dto: ActivityPageResponseDto): ActivityPageResponse {
  return {
    items: dto.i.map(mapActivityItem),
    page: dto.p,
    size: dto.s,
    totalItems: dto.t,
  };
}

/**
 * Maps FullActivityItemDto (short field names) to FullActivityItem (readable field names)
 */
export function mapFullActivityItem(dto: FullActivityItemDto): FullActivityItem {
  return {
    userId: dto.u,
    username: dto.n,
    outcomeId: dto.oi,
    outcomeName: dto.on,
    position: dto.p,
    side: dto.si,
    shares: dto.q,
    executedAt: dto.x,
    eventQuestion: dto.eq,
    eventIcon: dto.ei,
    eventSlug: dto.es,
    tokenName: dto.tn,
    isPrimary: dto.ip,
    price: dto.pr,
    eventVolume: dto.ev,
  };
}

/**
 * Maps FullActivityListResponseDto to FullActivityListResponse
 */
export function mapFullActivityListResponse(
  dto: FullActivityListResponseDto,
): FullActivityListResponse {
  return {
    items: dto.i.map(mapFullActivityItem),
    page: dto.p,
    size: dto.s,
    totalCount: dto.t,
  };
}

/**
 * Maps FullOrderHistoryDto (short field names) to FullOrderHistory (readable field names)
 */
export function mapFullOrderHistory(dto: FullOrderHistoryDto): FullOrderHistory {
  return {
    id: dto.i,
    userId: dto.u,
    eventId: dto.e,
    outcomeId: dto.o,
    assetId: dto.a,
    originalQuantity: dto.oq,
    side: dto.si,
    type: dto.t,
    price: dto.p,
    createdAt: dto.m,
    executionTime: dto.ex,
    status: dto.st,
    filledQuantity: dto.q,
    eventQuestion: dto.eq,
    eventIcon: dto.ei,
    eventSlug: dto.es,
    outcomeName: dto.on,
    tokenName: dto.tn,
    isPrimary: dto.ip,
  };
}

/**
 * Maps FullOrderHistoryListResponseDto to FullOrderHistoryListResponse
 */
export function mapFullOrderHistoryListResponse(
  dto: FullOrderHistoryListResponseDto,
): FullOrderHistoryListResponse {
  return {
    items: dto.i.map(mapFullOrderHistory),
    page: dto.p,
    size: dto.s,
    totalPages: dto.tp,
  };
}

/**
 * Maps TradeHistoryFullDto (short field names) to TradeHistoryFull (readable field names)
 */
export function mapTradeHistoryFull(dto: TradeHistoryFullDto): TradeHistoryFull {
  return {
    id: dto.i,
    userId: dto.u,
    eventId: dto.e,
    outcomeId: dto.o,
    assetId: dto.a,
    side: dto.si,
    quantity: dto.q,
    price: dto.p,
    executionTime: dto.m,
    eventQuestion: dto.eq,
    eventIcon: dto.ei,
    eventSlug: dto.es,
    outcomeName: dto.on,
    tokenName: dto.tn,
    isPrimary: dto.ip,
    value: dto.v,
  };
}

/**
 * Maps TradeHistoryFullListResponseDto to TradeHistoryFullListResponse
 */
export function mapTradeHistoryFullListResponse(
  dto: TradeHistoryFullListResponseDto,
): TradeHistoryFullListResponse {
  return {
    items: dto.i.map(mapTradeHistoryFull),
    page: dto.p,
    size: dto.s,
    totalPages: dto.tp,
  };
}

/**
 * Maps LastTradeStatsItemDto (short field names) to LastTradeStatsItem (readable field names)
 */
export function mapLastTradeStatsItem(dto: LastTradeStatsItemDto): LastTradeStatsItem {
  return {
    userId: dto.u,
    tradeId: dto.tra,
    username: dto.n,
    outcomeId: dto.oi,
    outcomeName: dto.on,
    position: dto.p,
    side: dto.si,
    shares: dto.q,
    executedAt: dto.x,
    eventQuestion: dto.eq,
    eventIcon: dto.ei,
    eventSlug: dto.es,
    tokenName: dto.tn,
    isPrimary: dto.ip,
    price: dto.pr,
    eventVolume: dto.ev,
    userVolume: dto.uv,
    look: mapPlayerLook(dto.lk),
  };
}

/**
 * Maps LastTradeStatsResponseDto to LastTradeStatsResponse
 * Handles multiple response shapes:
 *  - compact wrapper: { i, p, s, t }
 *  - full-name wrapper: { items, page, size, totalCount }
 *  - plain array
 */
export function mapLastTradeStatsResponse(
  dto: LastTradeStatsResponseDto | LastTradeStatsItemDto[] | Record<string, unknown>,
): LastTradeStatsResponse {
  if (Array.isArray(dto)) {
    return {
      items: dto.map(mapLastTradeStatsItem),
      page: 0,
      size: dto.length,
      totalCount: dto.length,
    };
  }

  // API returns { items: [...], page, size, totalCount } with full key names
  const raw = dto as Record<string, unknown>;
  const items = (raw.i ?? raw.items ?? []) as LastTradeStatsItemDto[];
  const page = (raw.p ?? raw.page ?? 0) as number;
  const size = (raw.s ?? raw.size ?? 0) as number;
  const totalCount = (raw.t ?? raw.totalCount ?? 0) as number;

  return {
    items: items.map(mapLastTradeStatsItem),
    page,
    size,
    totalCount,
  };
}
